import { Check, X } from "lucide-react";
import { BackCoverFooter, PageShell, TopNav } from "@/app/components/brand-scaffold";
import { ContentHero } from "@/app/components/content-hero";
import { siteHeaderActions, siteNavItems } from "@/app/content";
import type { ComparePage } from "@/app/seo-compare";

export function CompareLayout({ page }: { page: ComparePage }) {
  return (
    <PageShell>
      <TopNav navItems={siteNavItems} actionItems={siteHeaderActions} homeHref="/" />

      <ContentHero
        eyebrow={page.eyebrow}
        eyebrowColor="#A3E635"
        titleTop={`Hotline vs`}
        titleBottom={page.otherName}
        bottomColor={page.otherColor}
        lead={page.tldr}
        meta={["TL;DR · 60-120 字", `${page.dimensions.length} 个维度对照`, `${page.faqs.length} 条页内 FAQ`]}
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Compare" },
          { label: page.eyebrow.replace(/^Compare · /, "") },
        ]}
      />

      <main className="relative z-10 mx-auto w-full max-w-5xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        {/* Key takeaways */}
        <section className="border-[6px] border-black bg-[#A3E635]/85 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-black">KEY TAKEAWAYS</p>
          <h2 className="mt-3 text-2xl font-black uppercase text-black sm:text-3xl">3 句话讲完</h2>
          <ul className="mt-5 grid gap-3 text-sm font-bold leading-7 text-black sm:text-base">
            {page.takeaways.map((t) => (
              <li key={t} className="flex items-start gap-3">
                <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center border-2 border-black bg-black">
                  <Check className="h-3 w-3 text-[#A3E635]" strokeWidth={4} />
                </span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Dimension table */}
        <section className="mt-12">
          <header>
            <p className="text-xs font-black uppercase tracking-[0.22em] text-zinc-500">DIMENSIONS</p>
            <h2 className="mt-2 text-2xl font-black uppercase text-black sm:text-3xl">维度对照</h2>
            <p className="mt-3 max-w-3xl text-sm font-bold leading-6 text-zinc-700 sm:text-base">
              每行包含「Hotline 怎么做」「{page.otherName} 怎么做」与「这个差异为什么对你重要」。
            </p>
          </header>

          <div className="mt-6 overflow-hidden border-4 border-black bg-white/85 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)]">
            {/* Header row */}
            <div className="grid grid-cols-1 border-b-4 border-black bg-black text-white sm:grid-cols-[1.2fr_1.6fr_1.6fr]">
              <div className="p-4 text-xs font-black uppercase tracking-[0.22em] text-zinc-300">维度</div>
              <div className="border-t-4 border-black p-4 text-xs font-black uppercase tracking-[0.22em] text-[#A3E635] sm:border-t-0 sm:border-l-4">
                HOTLINE
              </div>
              <div className="border-t-4 border-black p-4 text-xs font-black uppercase tracking-[0.22em] sm:border-t-0 sm:border-l-4" style={{ color: page.otherColor }}>
                {page.otherName}
              </div>
            </div>

            {page.dimensions.map((d, idx) => (
              <div
                key={d.label}
                className={`grid grid-cols-1 sm:grid-cols-[1.2fr_1.6fr_1.6fr] ${idx !== page.dimensions.length - 1 ? "border-b-4 border-black" : ""}`}
              >
                <div className="bg-[#F8F6EF] p-4 text-sm font-black uppercase tracking-[0.12em] text-black">
                  {d.label}
                </div>
                <div className="border-t-2 border-black bg-[#A3E635]/30 p-4 text-sm font-bold leading-6 text-zinc-900 sm:border-t-0 sm:border-l-4">
                  <p>{d.hotline}</p>
                  <p className="mt-3 text-[11px] font-black uppercase tracking-[0.18em] text-zinc-600">
                    为什么重要
                  </p>
                  <p className="mt-1 text-xs font-bold leading-5 text-zinc-700">{d.why}</p>
                </div>
                <div className="border-t-2 border-black bg-white p-4 text-sm font-bold leading-6 text-zinc-700 sm:border-t-0 sm:border-l-4">
                  {d.other}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* When to use which */}
        <section className="mt-12 grid gap-5 lg:grid-cols-2">
          <article className="border-[6px] border-black bg-[#A3E635]/85 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-black">WHEN HOTLINE</p>
            <h3 className="mt-3 text-xl font-black uppercase text-black sm:text-2xl">用 Hotline 当你想……</h3>
            <p className="mt-4 text-sm font-bold leading-7 text-black sm:text-base">{page.whenHotline}</p>
            <div className="mt-5 inline-flex items-center gap-2 border-2 border-black bg-black px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-[#A3E635]">
              <Check className="h-3.5 w-3.5" strokeWidth={4} />
              选 HOTLINE
            </div>
          </article>

          <article className="border-[6px] border-black bg-white/85 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
            <p className="text-xs font-black uppercase tracking-[0.22em]" style={{ color: page.otherColor }}>
              WHEN {page.otherName.toUpperCase()}
            </p>
            <h3 className="mt-3 text-xl font-black uppercase text-black sm:text-2xl">用 {page.otherName} 当你想……</h3>
            <p className="mt-4 text-sm font-bold leading-7 text-zinc-700 sm:text-base">{page.whenOther}</p>
            <div className="mt-5 inline-flex items-center gap-2 border-2 border-black bg-zinc-100 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-black">
              <X className="h-3.5 w-3.5" strokeWidth={4} />
              不必 HOTLINE
            </div>
          </article>
        </section>

        {/* Page-specific FAQ */}
        <section className="mt-12 border-[6px] border-black bg-white/85 p-6 shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-[#A3E635]">PAGE FAQ</p>
          <h2 className="mt-3 text-2xl font-black uppercase text-black sm:text-3xl">
            关于 {page.otherName} 的常见疑问
          </h2>
          <div className="mt-6 grid gap-4">
            {page.faqs.map((f) => (
              <article key={f.question} className="border-4 border-black bg-[#F8F6EF] p-5">
                <h3 className="text-base font-black text-black sm:text-lg">{f.question}</h3>
                <p className="mt-3 text-sm font-bold leading-7 text-zinc-700">{f.answer}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Next steps */}
        <aside className="mt-14 border-[6px] border-black bg-black p-6 text-white shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] sm:p-8">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-[#A3E635]">FROM HERE</p>
          <h2 className="mt-3 text-2xl font-black uppercase sm:text-3xl">动手最快</h2>
          <p className="mt-3 text-sm font-bold leading-6 text-zinc-300 sm:text-base">
            阅读对比是为了决定要不要试一次。下方两条 5 分钟路径直接给你跑通的命令——能跑通就有判断。
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            {page.nextLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`inline-flex items-center gap-2 border-4 border-black px-5 py-3 text-sm font-black uppercase shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5 ${
                  link.tone === "primary" ? "bg-[#A3E635] text-black" : "bg-white text-black"
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Cross-link other compare pages */}
          <div className="mt-8 border-t-2 border-white/20 pt-5">
            <p className="text-[11px] font-black uppercase tracking-[0.2em] text-zinc-400">其他对比</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {[
                { slug: "hotline-vs-mcp", label: "vs MCP" },
                { slug: "hotline-vs-skills", label: "vs Skills" },
                { slug: "hotline-vs-api", label: "vs API" },
                { slug: "hotline-vs-saas", label: "vs SaaS" },
              ]
                .filter((x) => x.slug !== page.slug)
                .map((x) => (
                  <a
                    key={x.slug}
                    href={`/compare/${x.slug}/`}
                    className="inline-flex items-center gap-1 border-2 border-white/40 bg-white/10 px-3 py-1.5 text-xs font-black uppercase text-white transition-colors hover:bg-white/20"
                  >
                    {x.label}
                  </a>
                ))}
            </div>
          </div>
        </aside>
      </main>

      <BackCoverFooter />
    </PageShell>
  );
}
